import { useState } from 'react';
import { SlidersHorizontalIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const PRESETS = [7, 30, 90, 180, 365] as const;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

function clampDays(value: number) {
  if (!Number.isFinite(value)) {
    return 30;
  }
  return Math.max(MIN_DAYS, Math.min(MAX_DAYS, Math.round(value)));
}

export function UsagePeriodSelector({
  days,
  onChange,
}: {
  days: number;
  onChange: (days: number) => void;
}) {
  const isPreset = (PRESETS as readonly number[]).includes(days);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [customInput, setCustomInput] = useState(String(days));

  function applyCustom() {
    const next = clampDays(Number(customInput));
    onChange(next);
    setCustomInput(String(next));
    setIsCustomOpen(false);
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-[var(--radius-surface-3xl)] bg-muted/50 p-1">
      {PRESETS.map((preset) => (
        <Button
          key={preset}
          type="button"
          size="sm"
          variant="ghost"
          aria-pressed={days === preset}
          onClick={() => onChange(preset)}
          className={cn(
            'h-8 rounded-full px-3 text-sm font-medium text-muted-foreground hover:bg-background/60 hover:text-foreground',
            days === preset && 'bg-background text-foreground shadow-sm hover:bg-background',
          )}
        >
          {preset}j
        </Button>
      ))}
      <Popover
        open={isCustomOpen}
        onOpenChange={(open) => {
          setIsCustomOpen(open);
          if (open) {
            setCustomInput(String(days));
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-pressed={!isPreset}
            className={cn(
              'h-8 gap-1.5 rounded-full px-3 text-sm font-medium text-muted-foreground hover:bg-background/60 hover:text-foreground',
              !isPreset && 'bg-background text-foreground shadow-sm hover:bg-background',
            )}
          >
            <SlidersHorizontalIcon className="size-3.5" />
            {isPreset ? 'Personnalisé' : `${days}j`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 space-y-3">
          <div className="space-y-1">
            <Label htmlFor="usage-custom-days" className="text-sm font-medium">
              Période personnalisée
            </Label>
            <p className="text-xs text-muted-foreground">Entre 1 et 365 jours.</p>
          </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              applyCustom();
            }}
          >
            <Input
              id="usage-custom-days"
              inputMode="numeric"
              value={customInput}
              onChange={(event) => setCustomInput(event.target.value)}
              className="h-9"
            />
            <Button type="submit" size="sm" className="h-9 shrink-0">
              Appliquer
            </Button>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
}
